/* ── BugSmash Widget Styles ─────────────────────────────────────────────────
   Scoped inside Shadow DOM.
   swPrimary = #4c5eff  |  dark panel = #454545  |  name modal = #EDEFFF
   ─────────────────────────────────────────────────────────────────────────── */

export const WIDGET_STYLES = `
  :host {
    all: initial;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  *, *::before, *::after { box-sizing: border-box; }

  /* ── Floating trigger button ── */
  .bs-trigger {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #202531;
    background: oklch(19.09% .0243 262.19);
    border: none;
    color: #f8f4e4;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(76,94,255,0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
    padding: 0;
    outline: none;
    user-select: none;
    overflow: hidden;
  }
  .bs-trigger-icon {
    width: 24px;
    height: 24px;
    display: block;
    flex: 0 0 auto;
    pointer-events: none;
  }
  .bs-trigger:hover { transform: scale(1.08); box-shadow: 0 6px 24px rgba(76,94,255,0.55); }
  .bs-trigger:focus-visible { outline: 2px solid #4c5eff; outline-offset: 3px; }
  .bs-trigger.bs-active { filter: brightness(0.92); }

  /* ── Trigger wrapper + "Comments disabled" tooltip ── */
  .bs-trigger-wrap {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 2147483640;
  }
  .bs-trigger-wrap--left {
    right: auto;
    left: 24px;
  }
  .bs-trigger-wrap .bs-trigger {
    position: static;
  }
  .bs-trigger-wrap--left .bs-trigger {
    right: auto;
    left: 0;
  }
  .bs-trigger-tooltip {
    display: none;
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    background: #1e1e1e;
    color: rgba(255,255,255,0.9);
    font-size: 12px;
    font-weight: 500;
    padding: 5px 10px;
    border-radius: 6px;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  }
  .bs-trigger-wrap--left .bs-trigger-tooltip {
    right: auto;
    left: 0;
  }
  .bs-trigger-wrap.bs-show-trigger-tooltip .bs-trigger-tooltip {
    display: block;
  }

  /* ── Viewport border — thin #4c5eff frame, no dark fill, pointer-events none ── */
  .bs-viewport-border {
    position: fixed;
    inset: 0;
    z-index: 2147483637;
    border: 3px solid #4c5eff;
    pointer-events: none;
    display: none;
    box-shadow: inset 0 0 0 1px rgba(76,94,255,0.15);
  }

  /* ── Element hover highlight — fixed, mirrors getBoundingClientRect() ── */
  .bs-highlight {
    position: fixed;
    z-index: 2147483638;
    outline: 2px dashed #4c5eff;
    outline-offset: -2px;
    border-radius: 4px;
    background: rgba(76,94,255,0.05);
    pointer-events: none;
    display: none;
    transition: top 0.04s, left 0.04s, width 0.04s, height 0.04s;
  }

  /* ── Hint bar (bottom-centre) ── */
  .bs-hint {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(69,69,69,0.78);
    color: rgba(255,255,255,0.88);
    font-size: 13px;
    font-weight: 500;
    padding: 7px 18px;
    border-radius: 20px;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 12px rgba(0,0,0,0.3);
    z-index: 2147483647;
    display: none;
  }

  /* ── Exit (×) button — top-right ── */
  .bs-exit-btn {
    position: fixed;
    top: 16px;
    right: 20px;
    z-index: 2147483647;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(30,30,30,0.75);
    border: 1.5px solid rgba(255,255,255,0.2);
    color: #fff;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    transition: background 0.15s;
    padding: 0;
  }
  .bs-exit-btn:hover { background: rgba(60,60,60,0.9); }
  .bs-exit-btn svg { width: 14px; height: 14px; }

  /* ── Numbered annotation pin — plain filled circle, centered on click point ── */
  .bs-pin {
    position: fixed;
    z-index: 2147483645;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #4c5eff;
    border: 2px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translate(-50%, -50%);
    pointer-events: auto;
    cursor: default;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }
  .bs-pin:hover {
    transform: translate(-50%, -50%) scale(1.18);
    box-shadow: 0 4px 14px rgba(76,94,255,0.5);
  }
  .bs-pin-number {
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    user-select: none;
  }

  /* ── Pin hover tooltip ── */
  .bs-pin-tooltip {
    position: fixed;
    z-index: 2147483647;
    background: #1e1e1e;
    color: rgba(255,255,255,0.92);
    font-size: 12.5px;
    line-height: 1.5;
    padding: 8px 12px;
    border-radius: 8px;
    max-width: 240px;
    white-space: pre-wrap;
    word-break: break-word;
    pointer-events: none;
    box-shadow: 0 4px 16px rgba(0,0,0,0.4);
    transform: translate(-50%, calc(-100% - 10px));
    display: none;
  }
  .bs-pin-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #1e1e1e;
  }

  /* ── Dark backdrop behind the comment modal only ── */
  .bs-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2147483646;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    display: none;
  }

  /* ── Comment box — dark, centered ── */
  .bs-comment-box {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2147483647;
    width: min(440px, 93vw);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    display: none;
    flex-direction: column;
    animation: bs-fade-in 0.12s ease;
  }
  @keyframes bs-fade-in {
    from { opacity: 0; transform: translate(-50%, -48%); }
    to   { opacity: 1; transform: translate(-50%, -50%); }
  }
  @keyframes bs-spin { to { transform: rotate(360deg); } }

  .bs-editor-area { background: #454545; padding: 16px 24px; min-height: 132px; }
  .bs-editor {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 15px;
    line-height: 1.55;
    font-family: inherit;
    min-height: 100px;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .bs-editor p,
  .bs-editor ol { margin: 0; }
  .bs-editor p + p,
  .bs-editor p + ol,
  .bs-editor ol + p,
  .bs-editor ol + ol { margin-top: 10px; }
  .bs-editor ol { padding-left: 20px; }
  .bs-editor li + li { margin-top: 4px; }
  .bs-editor strong,
  .bs-editor b { font-weight: 700; }
  .bs-editor em,
  .bs-editor i { font-style: italic; }

  .bs-divider { height: 1px; background: rgba(0,0,0,0.25); }

  /* ── Toolbar — formatting icons left, actions right ── */
  .bs-toolbar {
    background: #3a3a3a;
    min-height: 54px;
    padding: 8px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .bs-toolbar-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .bs-toolbar-right { display: flex; align-items: center; gap: 10px; margin-left: auto; }

  .bs-fmt-btn,
  .bs-mic-btn,
  .bs-assignee-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #fff;
    opacity: 0.6;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    padding: 0;
    font-family: inherit;
    font-size: 14px;
    transition: opacity 0.12s;
  }
  .bs-fmt-btn:hover:not(:disabled),
  .bs-fmt-btn.is-active,
  .bs-mic-btn:hover,
  .bs-mic-btn.is-recording { opacity: 1; }
  .bs-mic-btn.is-recording {
    color: #ff8a8a;
  }
  .bs-attach-btn[hidden] { display: none; }
  .bs-attachment-input { display: none; }
  .bs-attachment-preview { position: relative; width: 32px; height: 32px; flex: 0 0 auto; }
  .bs-attachment-img { width: 100%; height: 100%; border-radius: 4px; object-fit: cover; display: block; }
  .bs-remove-attachment {
    position: absolute; top: -7px; right: -7px; width: 18px; height: 18px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.65); background: #e5e5e5; color: #303030; display: none;
    align-items: center; justify-content: center; padding: 0; cursor: pointer;
  }
  .bs-attachment-preview:hover .bs-remove-attachment { display: flex; }
  .bs-assignee-btn { gap: 6px; width: auto; padding: 0 2px; opacity: 1; }
  .bs-assignee-btn { cursor: default; }
  .bs-assignee-avatar {
    width: 24px; height: 24px; border-radius: 50%; background: rgba(255,255,255,0.1); display: flex;
    align-items: center; justify-content: center; color: rgba(255,255,255,0.6);
  }
  .bs-assignee-chevron { display: flex; color: rgba(255,255,255,0.5); }

  /* Blue square send button */
  .bs-submit-btn {
    background: #4c5eff;
    color: #fff;
    border: none;
    cursor: pointer;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: opacity 0.15s, transform 0.1s;
    flex-shrink: 0;
  }
  .bs-submit-btn svg { width: 16px; height: 16px; }
  .bs-submit-btn:hover:not(:disabled) { opacity: 0.88; transform: scale(1.04); }
  .bs-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

  .bs-error { font-size: 12px; color: #ff6b6b; padding: 4px 20px 8px; background: #454545; }

  .bs-toast-container {
    position: fixed; right: 1em; bottom: 1em; width: 320px; padding: 4px;
    z-index: 2147483647; color: #fff; box-sizing: border-box; display: none;
    pointer-events: none;
  }
  .bs-toast {
    min-height: 64px; padding: 10px; border-radius: 10px / 13px;
    background: hsla(234, 54%, 39%, 1); color: #fff; box-shadow: 0 1px 10px rgba(0,0,0,.1), 0 2px 15px rgba(0,0,0,.05);
    cursor: pointer; display: flex; justify-content: space-between; position: relative;
    overflow: hidden; pointer-events: auto; font-family: inherit;
  }
  .bs-toast-body {
    align-items: center; display: flex; flex: 1 1 auto; margin: auto 0 auto 7px;
    padding: 6px; font-size: 14px; font-weight: 500; width: 100%;
  }
  .bs-toast-icon { display: flex; flex-shrink: 0; margin-right: 10px; width: 20px; }
  .bs-toast-icon svg { width: 20px; height: 20px; fill: currentColor; }
  .bs-toast-message { flex: 1; }
  .bs-toast-close {
    align-self: flex-start; background: transparent; border: 0; color: #fff; cursor: pointer;
    opacity: .7; outline: none; padding: 0; transition: opacity .3s ease;
  }
  .bs-toast-close:hover, .bs-toast-close:focus { opacity: 1; }
  .bs-toast-close svg { fill: currentColor; height: 16px; width: 14px; }
  .bs-toast-progress {
    position: absolute; bottom: 0; left: 0; height: 5px; width: 100%;
    background: hsla(0,0%,100%,.7); opacity: .7; transform-origin: left;
  }
  .bs-toast-container.is-visible .bs-toast-progress { animation: bs-toast-progress 500ms linear forwards; }
  @keyframes bs-toast-progress { from { transform: scaleX(1); } to { transform: scaleX(0); } }
  @media only screen and (max-width: 480px) {
    .bs-toast-container { left: 0; right: auto; bottom: 0; width: 100vw; padding: 0; }
    .bs-toast { border-radius: 0; margin-bottom: 0; }
  }

  /* ── Anonymous name modal ── */
  .bs-name-overlay {
    position: fixed;
    inset: 0;
    z-index: 2147483646;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    display: none;
  }
  .bs-name-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2147483647;
    width: min(400px, 93vw);
    background: #EDEFFF;
    border-radius: 12px;
    padding: 32px 40px;
    display: none;
    flex-direction: column;
    align-items: center;
    animation: bs-fade-in 0.15s ease;
  }
  .bs-name-inner { width: 100%; position: relative; }
  .bs-name-close {
    position: absolute;
    top: -16px;
    right: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: rgba(20,20,20,0.8);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 4px;
    transition: color 0.15s;
  }
  .bs-name-close:hover { color: #141414; }
  .bs-name-close svg { width: 18px; height: 18px; }
  .bs-name-title { font-size: 22px; font-weight: 900; color: #141414; width: 100%; line-height: 1.2; margin: 0 0 8px; }
  .bs-name-subtitle { font-size: 13px; color: rgba(20,20,20,0.6); width: 100%; line-height: 1.5; margin: 0; }
  .bs-name-form { width: 100%; margin-top: 20px; }
  .bs-name-input {
    width: 100%;
    border-radius: 10px;
    background: #fff;
    border: 1.5px solid rgba(76,94,255,0.15);
    padding: 14px 16px;
    font-size: 14px;
    color: #141414;
    font-family: inherit;
    outline: none;
    transition: border-color 0.15s;
  }
  .bs-name-input:focus { border-color: #4c5eff; }
  .bs-name-input::placeholder { color: rgba(20,20,20,0.4); }
  .bs-name-submit {
    width: 100%;
    margin-top: 20px;
    padding: 12px;
    border-radius: 9999px;
    background: #4c5eff;
    color: #fff;
    font-size: 17px;
    font-weight: 400;
    font-family: inherit;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .bs-name-submit:hover:not(:disabled) { opacity: 0.9; }
  .bs-name-submit:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/** Custom marker/pin cursor — same PNG + hotspot (4 29) used by the proxy iframe */
export const COMMENT_MODE_CURSOR =
  `url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAM1BMVEVHcExMXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv9MXv/ZjzXpAAAAEHRSTlMAEqXf7TzI/R5981gJu4qOTvZjlgAAAK1JREFUOMuFk9kWgzAIBSELZFX+/2urts1RozCvjGCSC8CfFnP3hbn4nmODO1h7kEHoFa/1SiwXmOr586XIHS7LaIJOHnGo14eR+U3gvNdjkFdC3I7vRME1tcHRYhWVFUgXCIIuBGBdYFswR5g/aR7TvCjzqs3Hsp/bDowZuT20D1NOoQVIfhp/if0klPvi/ARPfoPcvHqHUFxsmBLOe/kVqD5VhuAzgkJy8a30Aa7gK/khdXfVAAAAAElFTkSuQmCC') 4 29, auto`;
